import {
  BrokerId,
  ClientId,
  ClientMetadata,
  HandshakeInit,
  HandshakeResult,
  IMessage,
  RequestTopicSubscriptionsPayload,
  TopicName,
  TopicSubscribePayload,
  TopicSubscriptions,
  TopicUnsubscribePayload,
} from 'magpie-types';
import { ObservableMap } from 'reactive-lib/collections/observable-map';
import { Subject } from 'reactive-lib/observable/subject';
import { filter } from 'reactive-lib/operators/filter';
import { WINDOW_BROKER_LOGGER_NAME } from '../config';
import { Logger } from 'simple-logging-lib';
import { MessageLink } from '../models';
import { generateClientId, getUUID } from '../utils';
import { WindowBrokerInterface } from './window-broker-interface';
import { WindowBrokerConnector } from './window-broker-proxy';
import { CrossTabLink } from './cross-tab-link/cross-tab-link';

export class WindowBroker implements WindowBrokerInterface {
  protected readonly logger = Logger.getLogger(WINDOW_BROKER_LOGGER_NAME);
  #brokerId: BrokerId;
  #allMessages: MessageLink = new Subject();
  #clientSubscriberMap = new Map<ClientId, MessageLink>();
  #topicSubscriptionState = new ObservableMap<TopicName, Set<ClientId>>();
  #clientMetadataState = new ObservableMap<ClientId, ClientMetadata>();
  #crossTabLink: CrossTabLink;

  static #instance: WindowBroker;
  static #connector: WindowBrokerConnector;

  private constructor() {
    this.logger.debug('WindowBroker instance created');
    this.#brokerId = getUUID();
    WindowBroker.#connector = WindowBrokerConnector.getInstance(this);
    WindowBroker.#connector.initialize();
    this.#crossTabLink = CrossTabLink.getInstance();
    this.#crossTabLink.connect({
      messageLink: this.#allMessages,
      brokerId: this.#brokerId,
    });
  }

  static getInstance(): WindowBroker {
    if (!WindowBroker.#instance) {
      WindowBroker.#instance = new WindowBroker();
    }
    return WindowBroker.#instance;
  }

  handshake = (
    { clientMetadata }: HandshakeInit,
    link: MessageLink
  ): Promise<HandshakeResult> => {
    const clientId = generateClientId();
    this.#clientSubscriberMap.set(clientId, link);
    this.#clientMetadataState.set(clientId, clientMetadata);

    this.#allMessages
      .asObservable()
      .pipe(
        filter((message) => {
          const topicSubscribers =
            this.#topicSubscriptionState.get(message.topic) ?? new Set();
          return topicSubscribers.has(clientId);
        })
      )
      .subscribe(link);

    return Promise.resolve({ clientId, brokerId: this.#brokerId });
  };

  topicSubscribe = ({
    clientId,
    topic,
  }: TopicSubscribePayload): Promise<void> => {
    const topicSubscribers =
      this.#topicSubscriptionState.get(topic) ?? new Set();
    topicSubscribers.add(clientId);
    this.#topicSubscriptionState.set(topic, topicSubscribers);
    return Promise.resolve();
  };

  topicUnsubscribe = ({
    clientId,
    topic,
  }: TopicUnsubscribePayload): Promise<void> => {
    const topicSubscribers =
      this.#topicSubscriptionState.get(topic) ?? new Set();
    topicSubscribers.delete(clientId);
    this.#topicSubscriptionState.set(topic, topicSubscribers);
    return Promise.resolve();
  };

  getTopicSubscriptions = ({
    clientId,
    portalId,
  }: RequestTopicSubscriptionsPayload): Promise<TopicSubscriptions> => {
    const entries = Array.from(this.#topicSubscriptionState.entries());
    const topicNames = entries
      .filter(([__topic, subscribers]) => subscribers.has(clientId))
      .map(([topic]) => topic);
    return Promise.resolve({ clientId, portalId, topicNames });
  };

  close = (clientId: ClientId): Promise<void> => {
    this.#clientSubscriberMap.delete(clientId);
    this.#clientMetadataState.delete(clientId);

    this.#topicSubscriptionState.forEach((subscribers) => {
      subscribers.delete(clientId);
    });

    return Promise.resolve();
  };

  sendMessage = (message: IMessage): Promise<void> => {
    const { topic } = message;
    this.logger.debug(
      `Broker sending the message '${message.type}' on topic '${topic}'`,
      message
    );
    this.#allMessages.next(message);
    return Promise.resolve();
  };

  reset = (): Promise<void> => {
    this.#clientSubscriberMap.clear();
    this.#clientMetadataState.clear();
    this.#topicSubscriptionState.clear();
    WindowBroker.#connector.close();
    // @ts-ignore - Resetting the singleton instance for testing purposes
    WindowBroker.#instance = undefined;
    return Promise.resolve();
  };

  getBrokerId = (): Readonly<BrokerId> => this.#brokerId;
}
